import { fmtUSD } from "@/lib/format";
import { ArrowRight, TrendingDown } from "lucide-react";

/**
 * Asymmetric, staggered waterfall visualisation of the 3-gate model.
 */
export const WaterfallVisualizer = ({ breakdown }) => {
    if (!breakdown) return null;
    const { revenue, supplier_payment = 0, capital_recovery: cap, expense_recovery: exp, net_profit, profit_split, total_payout, is_loss, shortfall } = breakdown;

    const capTotal = (cap?.bashari || 0) + (cap?.ido || 0);
    const expTotal = (exp?.bashari || 0) + (exp?.ido || 0);
    const scale = Math.max(revenue, supplier_payment + capTotal + expTotal + net_profit, 1);

    const w = (v) => `${Math.max(4, Math.min(100, (v / scale) * 100)).toFixed(1)}%`;

    return (
        <div className="space-y-6 font-sans" data-testid="waterfall-visualizer">
            <div>
                <div className="text-[10px] uppercase tracking-widest text-stone-500">Revenue</div>
                <div className="text-3xl font-light num text-stone-900">{fmtUSD(revenue)}</div>
            </div>

            {is_loss && (
                <div className="flex items-center gap-2 border-l-2 border-rose-600 bg-rose-50 px-3 py-2 text-rose-700 text-xs uppercase tracking-widest" data-testid="waterfall-loss-flag">
                    <TrendingDown className="h-4 w-4" strokeWidth={1.5} />
                    Loss scenario — shortfall {fmtUSD(shortfall)}. Proportional recovery only.
                </div>
            )}

            {supplier_payment > 0.01 && (
                <>
                    <SupplierStage amount={supplier_payment} width={w(supplier_payment)} testid="waterfall-gate-supplier" />
                    <div className="pl-3"><ArrowRight className="h-4 w-4 text-stone-400" strokeWidth={1.5} /></div>
                </>
            )}

            <Stage
                index={supplier_payment > 0.01 ? 2 : 1}
                label="Capital Recovery"
                sublabel="Reimburse partner cash paid to supplier"
                width={w(capTotal)}
                accent="emerald"
                bashari={cap?.bashari || 0}
                ido={cap?.ido || 0}
                testid="waterfall-gate-1"
            />
            <div className="pl-3"><ArrowRight className="h-4 w-4 text-stone-400" strokeWidth={1.5} /></div>
            <Stage
                index={supplier_payment > 0.01 ? 3 : 2}
                label="Expense Recovery"
                sublabel="Reimburse item-level costs"
                width={w(expTotal)}
                accent="amber"
                bashari={exp?.bashari || 0}
                ido={exp?.ido || 0}
                testid="waterfall-gate-2"
            />
            <div className="pl-3"><ArrowRight className="h-4 w-4 text-stone-400" strokeWidth={1.5} /></div>
            <Stage
                index={supplier_payment > 0.01 ? 4 : 3}
                label="Net Profit"
                sublabel="Split 50 / 50"
                width={w(Math.max(net_profit, 0))}
                accent={net_profit > 0 ? "sky" : "zinc"}
                bashari={profit_split?.bashari || 0}
                ido={profit_split?.ido || 0}
                testid="waterfall-gate-3"
                totalOverride={net_profit}
            />

            <div className="border-t border-stone-200 pt-4">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Total Payout to partners</div>
                <div className="grid grid-cols-2 gap-4">
                    <PayoutTile name="Bashari" amount={total_payout?.bashari || 0} testid="payout-bashari" />
                    <PayoutTile name="Ido" amount={total_payout?.ido || 0} testid="payout-ido" />
                </div>
            </div>
        </div>
    );
};

const SupplierStage = ({ amount, width, testid }) => (
    <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] items-center gap-4" data-testid={testid}>
        <div className="text-[10px] uppercase tracking-widest text-stone-500">Gate 1</div>
        <div className="border-l-2 pl-4 py-3 border-rose-500/70 bg-rose-50" style={{ width }}>
            <div className="text-sm text-stone-800">Supplier balance</div>
            <div className="text-[11px] text-stone-500 uppercase tracking-wider">Clear the unpaid supplier debt first</div>
            <div className="text-xl font-light num mt-1 text-rose-700" data-testid={`${testid}-amount`}>{fmtUSD(amount)}</div>
        </div>
        <div className="text-xs text-stone-500 md:text-right">External outflow</div>
    </div>
);

const accentMap = {
    emerald: "border-emerald-500/70 bg-emerald-50",
    amber: "border-amber-500/70 bg-amber-100",
    sky: "border-sky-500/70 bg-sky-50",
    zinc: "border-stone-400 bg-stone-100",
};

const Stage = ({ index, label, sublabel, width, accent, bashari, ido, testid, totalOverride }) => {
    const total = totalOverride ?? (bashari + ido);
    return (
        <div className="grid grid-cols-1 md:grid-cols-[80px_1fr_auto] items-center gap-4" data-testid={testid}>
            <div className="text-[10px] uppercase tracking-widest text-stone-500">Gate {index}</div>
            <div className={`border-l-2 pl-4 py-3 ${accentMap[accent]}`} style={{ width }}>
                <div className="text-sm text-stone-800">{label}</div>
                <div className="text-[11px] text-stone-500 uppercase tracking-wider">{sublabel}</div>
                <div className="text-xl font-light num mt-1">{fmtUSD(total)}</div>
            </div>
            <div className="text-xs text-stone-500 space-y-1 md:text-right">
                <div>Bashari <span className="num text-stone-800" data-testid={`${testid}-bashari`}>{fmtUSD(bashari)}</span></div>
                <div>Ido <span className="num text-stone-800" data-testid={`${testid}-ido`}>{fmtUSD(ido)}</span></div>
            </div>
        </div>
    );
};

const PayoutTile = ({ name, amount, testid }) => (
    <div className="border border-stone-200 p-4 bg-white">
        <div className="text-[10px] uppercase tracking-widest text-stone-500">{name}</div>
        <div className="text-2xl font-light num text-stone-900 mt-1" data-testid={testid}>{fmtUSD(amount)}</div>
    </div>
);

export default WaterfallVisualizer;
